import type { ReactNode } from 'react'

import { useEffect, useMemo } from 'react'
import { I18nextProvider } from 'react-i18next'

import i18n from '../../i18n'

export const DEFAULT_NS = 'default'

/**
 * When namespace is not 'default', resolves `t('key')` as: <namespace> → common → default.
 * When namespace is 'default', renders children as-is (no override needed).
 */
export const PrimaryNamespaceTranslations = (props: {
	namespace: string
	children: ReactNode
}) => {
	const { namespace, children } = props
	const enabled = namespace !== DEFAULT_NS

	const scopedI18n = useMemo(
		() =>
			enabled
				? i18n.cloneInstance({
						defaultNS: namespace,
						fallbackNS: ['common', DEFAULT_NS],
					})
				: null,
		[enabled, namespace],
	)

	useEffect(() => {
		if (!scopedI18n) return
		const handleLanguageChange = (lng: string) => {
			scopedI18n.changeLanguage(lng)
		}
		i18n.on('languageChanged', handleLanguageChange)
		return () => {
			i18n.off('languageChanged', handleLanguageChange)
		}
	}, [scopedI18n])

	if (!enabled) {
		return <>{children}</>
	}

	return <I18nextProvider i18n={scopedI18n!}>{children}</I18nextProvider>
}
